import { useCallback, useMemo, useSyncExternalStore } from 'react';

interface Toast {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

// File de notifications PARTAGÉE par toute l'application. Chaque appel à
// useToast avait auparavant son propre useState : le ToastContainer lisait
// une liste toujours vide, et aucun message (erreur d'enregistrement,
// validation refusée...) n'apparaissait jamais à l'écran.
let toastList: Toast[] = [];
const listeners = new Set<() => void>();

const setToastList = (next: Toast[]) => {
  toastList = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => toastList;

export function useToast() {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // ✅ Stabiliser showToast avec useCallback pour éviter les boucles infinies
  const showToast = useCallback(({ title, message, type }: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2, 11);
    setToastList([...toastList, { id, title, message, type }]);

    // Une erreur reste plus longtemps : c'est elle qu'il faut avoir le temps de lire.
    setTimeout(() => {
      setToastList(toastList.filter((t) => t.id !== id));
    }, type === 'error' ? 8000 : 5000);
  }, []);

  // ✅ Retourner un objet mémorisé pour éviter les re-renders
  return useMemo(() => ({ toasts, showToast }), [toasts, showToast]);
}
